"use client";
import { IconBook } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCodeOutline18 } from "nucleo-ui-outline-18";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { JsonSchemaProperty } from "./types";

interface X402OutputSectionProps {
  integration: {
    resultSchema?: {
      properties?: Record<string, JsonSchemaProperty>;
      required?: string[];
    };
  };
  searchQuery: string;
  exampleOutput?: unknown;
}

/** Infer the type of a value for display */
function inferType(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

/** Parse example output into schema-like fields */
function parseExampleToFields(example: unknown): Array<{ name: string; type: string }> {
  if (!example || typeof example !== "object" || Array.isArray(example)) {
    return [];
  }

  return Object.entries(example as Record<string, unknown>).map(([name, value]) => ({
    name,
    type: inferType(value),
  }));
}

/** Extract nested fields from schema properties */
function getNestedFieldsFromSchema(
  schema: JsonSchemaProperty | undefined,
): Array<{ name: string; type: string; description?: string }> {
  if (!schema?.properties) {
    return [];
  }

  return Object.entries(schema.properties).map(([name, prop]) => ({
    name,
    type: prop.type || "string",
    description: prop.description,
  }));
}

/**
 * NestedFieldsSection - Reusable component for showing nested fields under an object field.
 */
function NestedFieldsSection({
  title,
  fields,
  exampleOutput,
  showExample = false,
}: {
  title: string;
  fields: Array<{ name: string; type: string; description?: string }>;
  exampleOutput?: unknown;
  showExample?: boolean;
}) {
  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 space-y-3 border-l-2 border-green-200 pl-4 dark:border-green-800">
      <p className="label label-xs">{title}</p>
      {fields.map(field => (
        <div
          key={field.name}
          className="dark:bg-ds-neutral-800/50 flex items-center justify-between rounded-md bg-zinc-50 px-3 py-2"
        >
          <div className="flex flex-col gap-0.5">
            <code className="text-ds-text-secondary font-mono text-sm">{field.name}</code>
            {field.description && <span className="label-xs">{field.description}</span>}
          </div>
          <Badge variant="success">{field.type}</Badge>
        </div>
      ))}
      {/* Collapsible example output - only for resource field */}
      {showExample && exampleOutput !== undefined && exampleOutput !== null && (
        <Accordion type="single" collapsible className="mt-2">
          <AccordionItem value="example" className="border-0">
            <AccordionTrigger className="py-2 hover:no-underline">
              <div className="text-ds-text-muted flex items-center gap-2 text-xs font-medium">
                <IconCodeOutline18 className="h-3.5 w-3.5" />
                <span>Example Response</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="border-ds-border-default overflow-hidden rounded-lg border">
                <pre className="dark:bg-ds-neutral-900 max-h-64 overflow-auto bg-zinc-50 p-3 text-xs">
                  <code className="text-zinc-800 dark:text-zinc-200">{JSON.stringify(exampleOutput, null, 2)}</code>
                </pre>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}

/**
 * X402OutputSection - Output section for x402 endpoints.
 * Follows the same UX pattern as regular actions.
 * Shows nested fields for object types (resource, paymentDetails).
 */
export function X402OutputSection({ integration, searchQuery, exampleOutput }: X402OutputSectionProps) {
  const properties = integration.resultSchema?.properties || {};
  const allFields = Object.entries(properties);

  // Parse example output for showing under "resource" field
  const resourceFieldsFromExample = parseExampleToFields(exampleOutput);

  // Filter by search query
  const filteredFields = searchQuery.trim()
    ? allFields.filter(
        ([fieldName, fieldSchema]) =>
          fieldName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          fieldSchema.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : allFields;

  if (allFields.length === 0 && !exampleOutput) {
    return null;
  }

  return (
    <div>
      {/* Output Section - same as regular actions */}
      {allFields.length > 0 && (
        <div className="mb-8">
          <div className="mb-4 flex items-center gap-2">
            <IconBook className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-ds-text-primary text-xl font-semibold">Output</h2>
          </div>

          {filteredFields.length === 0 ? (
            <p className="body-sm">No fields match your search.</p>
          ) : (
            <Accordion type="multiple" defaultValue={filteredFields.map(([name]) => name)}>
              {filteredFields.map(([fieldName, fieldSchema]) => {
                // Get nested fields from schema for object types
                const schemaNestedFields = getNestedFieldsFromSchema(fieldSchema);

                // For "resource" field, prefer example-derived fields if available
                const nestedFields =
                  fieldName === "resource" && resourceFieldsFromExample.length > 0
                    ? resourceFieldsFromExample
                    : schemaNestedFields;

                const hasNestedFields = nestedFields.length > 0;

                return (
                  <AccordionItem key={fieldName} value={fieldName} className="border-ds-border-default border-b">
                    <AccordionTrigger className="py-4 hover:no-underline">
                      <div className="flex w-full items-center justify-between pr-4">
                        <code className="text-ds-text-primary dark:from-ds-neutral-700 dark:to-ds-neutral-700 rounded bg-gradient-to-br from-green-50 to-green-100 px-2 py-1 font-mono text-sm font-semibold">
                          {fieldName}
                        </code>
                        <Badge variant="success" className="px-2.5">
                          {fieldSchema.type || "string"}
                        </Badge>
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="space-y-3 pb-4">
                      {fieldSchema.description && <p className="body-sm">{fieldSchema.description}</p>}

                      {fieldSchema.format && (
                        <div className="label-xs">
                          Format:{" "}
                          <code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1 py-0.5 font-mono">
                            {fieldSchema.format}
                          </code>
                        </div>
                      )}

                      {fieldSchema["x-semantic-type"] && (
                        <div className="label-xs">
                          Semantic Type:{" "}
                          <code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1 py-0.5 font-mono">
                            {fieldSchema["x-semantic-type"]}
                          </code>
                        </div>
                      )}

                      {/* Show nested fields for object types */}
                      {hasNestedFields && (
                        <NestedFieldsSection
                          title={fieldName === "resource" ? "Response Fields" : "Fields"}
                          fields={nestedFields}
                          exampleOutput={fieldName === "resource" ? exampleOutput : undefined}
                          showExample={fieldName === "resource"}
                        />
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      )}
    </div>
  );
}
